import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { AnalysisScope, type AnalysisIssue, type MessageAnalysisResult } from '@k-ucap/shared';
import { aggregateGroupSeverity, getSeverityColor } from './utils.js';

export interface AnalysisTreeProps {
  result: MessageAnalysisResult | null;
  selectedIssue: AnalysisIssue | null;
  onSelectIssue: (issue: AnalysisIssue) => void;
}

interface GroupConfig {
  key: string;
  scope: AnalysisScope;
  label: string;
}

const groups: GroupConfig[] = [
  { key: 'definitionResults', scope: AnalysisScope.DEFINITION, label: 'Definition' },
  { key: 'headerResults', scope: AnalysisScope.HEADER, label: 'Header' },
  { key: 'fieldResults', scope: AnalysisScope.FIELD, label: 'Fields' },
  { key: 'roleResults', scope: AnalysisScope.ROLE, label: 'Role' },
  { key: 'sequenceResults', scope: AnalysisScope.SEQUENCE, label: 'Sequence' },
  { key: 'timingResults', scope: AnalysisScope.TIMING, label: 'Timing' },
];

export const AnalysisTree = ({ result, selectedIssue, onSelectIssue }: AnalysisTreeProps) => {
  const [open, setOpen] = useState<Record<string, boolean>>({
    [AnalysisScope.DEFINITION]: true,
    [AnalysisScope.HEADER]: true,
    [AnalysisScope.FIELD]: true,
    [AnalysisScope.ROLE]: true,
    [AnalysisScope.SEQUENCE]: true,
    [AnalysisScope.TIMING]: true,
  });

  if (!result) {
    return (
      <Paper sx={{ height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">분석할 메시지를 선택하세요.</Typography>
      </Paper>
    );
  }

  const toggle = (scope: AnalysisScope) => {
    setOpen((prev) => ({ ...prev, [scope]: !prev[scope] }));
  };

  return (
    <Paper sx={{ height: '100%', p: 2, overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Analysis Tree
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        PoC Preview Encoder 기준 분석
      </Typography>
      <List dense disablePadding>
        {groups.map((group) => {
          const issues = (result as unknown as Record<string, AnalysisIssue[]>)[group.key] ?? [];
          const severity = aggregateGroupSeverity(issues);
          const isOpen = open[group.scope] ?? true;
          return (
            <Box key={group.scope}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => toggle(group.scope)} sx={{ pr: 1 }}>
                  <IconButton size="small" edge="end" sx={{ mr: 1 }}>
                    {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {group.label}
                        </Typography>
                        <Chip label={severity} size="small" color={getSeverityColor(severity)} />
                        <Typography variant="caption" color="text.secondary">
                          {issues.length} items
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List dense disablePadding sx={{ pl: 4 }}>
                  {issues.map((issue, idx) => (
                    <ListItem key={`${issue.code}-${idx}`} disablePadding>
                      <ListItemButton
                        selected={selectedIssue?.code === issue.code && selectedIssue?.message === issue.message}
                        onClick={() => onSelectIssue(issue)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={issue.severity} size="small" color={getSeverityColor(issue.severity)} />
                              <Typography variant="body2" component="span">
                                {issue.code}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" component="span">
                              {issue.path || '-'}: {issue.message}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Paper>
  );
};
